import React, { useState, useEffect } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import camelcaseKeys from 'camelcase-keys';
import { FranchiseeState } from '../../context/Franchisee/franchisee.types';
import Gataware from '../../SDKs/Gataware';
import GatherUp from '../../SDKs/GatherUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import SVG from 'react-inlinesvg';
import { Container, Row, Col, Card, Accordion } from 'react-bootstrap';
import Layout from '../../components/Layout';
import Head from '../../components/head';
import Section from '../../components/Section';
import PageTitle from '../../components/PageTitle';
import SwooptyBg from '../../components/SwooptyBg';
import ReviewStars, { animateStars } from '../../components/ReviewStars';
import StaticReviewStars from '../../components/ReviewStars/staticStars';
import OpenGraphTitle from '../../components/OpenGraphTitle';
import OpenGraphDescription from '../../components/OpenGraphDescription';
import reviews from '../../data/reviews/reviews.json';
// import { pink500 } from '../../ui/colors';
import { FranchiseeProvider } from '../../context/Franchisee';
import ReviewLinks from '../../components/ReviewLinks';
import * as Sentry from '@sentry/node';
import ReviewsPagination from '../../components/ReviewsPagination';
import { Duration } from 'luxon';
// const anonymousAvatar = '/images/anonymous-avatar.svg';

export const getStaticPaths: GetStaticPaths = async () => {
  // const response = await Gataware.getLocations()
  //   .then(({ data }) => JSON.parse(data))
  //   .then((data) => data.data.map((franchisee) => camelcaseKeys(franchisee)));

  // const paths = response.map(({ slug }) => ({ params: { franchiseeSlug: slug } }));
  const popularStores: Array<string> = ['fort-worth'];
  const paths = popularStores.map((storeSlug) => ({
    params: { franchiseeSlug: storeSlug },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { franchiseeSlug } = params;

  const response = await Gataware.getLocations({
    slugs: [franchiseeSlug.toLocaleString()],
  }).then(({ data }) => JSON.parse(data));

  const franchisees = await response.data
    .filter(({ slug }) => franchiseeSlug === slug)
    .map((franchisee) => camelcaseKeys(franchisee));

  if (!franchisees.length) {
    return {
      notFound: true,
      revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
    };
  }

  const [franchisee] = franchisees;

  let url = franchisee.url + '.com';

  // Let's try to get the list of businesses
  const businessesId = await GatherUp.getBusinesses()
    .then((res) => {
      if (res === null) return;
      let matchBusinessWithFranchisee = res.find((business) => (business.businessWebsiteURL.replace(/(^\w+:|^)\/\//, '') === url))

      if (matchBusinessWithFranchisee === undefined) {
        return null;
      }

      // console.log(matchBusinessWithFranchisee)
      let businessId = matchBusinessWithFranchisee.businessId;

      return businessId;
    })
    .catch((error) => {
      //   console.log(error);
      Sentry.captureException(error);
    });

  const storeReviews = await GatherUp.getReviews({
    businessId: businessesId,
  });

  return {
    props: {
      franchisee,
      storeReviews,
      businessesId,
    },
    revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
  };
};

interface ReviewsPageProps {
  /* add about page props here */
  franchisee: FranchiseeState;
  storeReviews;
  businessesId;
}

const Reviews: React.FC<ReviewsPageProps> = ({
  franchisee,
  storeReviews,
  businessesId,
}: ReviewsPageProps) => {
  const [firstReview] = storeReviews;
  const [defaultFirstReview] = reviews.defaultReviews;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [reviewsPerPage] = useState<number>(6);

  // cycle stars animation
  useEffect(animateStars);

  // control toggle icon on header
  const [activeEventKey, setActiveEventKey] = useState('-1');
  function toggleEventKey(eventKey: string): void {
    if (activeEventKey === eventKey) {
      setActiveEventKey('-1');
    } else {
      setActiveEventKey(eventKey);
    }
  }

  // Get current posts
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = storeReviews.slice(
    indexOfFirstReview,
    indexOfLastReview
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    setCurrentPage(1);
  }, [franchisee.slug]);

  return (
    <FranchiseeProvider>
      <Layout alternateFooter>
        <Head
          slug='reviews'
          title={`Reviews | Two Maids | ${franchisee.name}`}
          description={`What Our Clients from Two Maids ${franchisee.name} are saying.`}
        />
        <OpenGraphTitle
          title={`Reviews | Two Maids | ${franchisee.name}`}
        />
        <OpenGraphDescription>
          {`What Our Clients from Two Maids  ${franchisee.name} are
          saying.`}
        </OpenGraphDescription>
        <div className='top-background-dots'></div>
        <Section className='first-review-section'>
          {/* <PageTitle className='sr-only mb-5'>Reviews</PageTitle> */}
          <PageTitle className='mb-2'>What Our Clients Say</PageTitle>
          {businessesId === null || storeReviews.length === 0 ? (
              <Col xs={9} md={7} lg={5} className='mb-5 mb-lg-0'>
                {defaultFirstReview?.imageUrl && (
                  <>
                  <img
                    src={defaultFirstReview?.imageUrl}
                    className='rounded-circle'
                    width='720'
                    height='688'
                    alt={defaultFirstReview?.name}
                    />
                    <div className='first-review-content'>
                      <div className='review__content'>
                        {defaultFirstReview?.content}
                      </div>
                      <div className='review__name'>
                        {defaultFirstReview?.name}
                      </div>
                      <div className='review__location'>
                        {defaultFirstReview?.location}
                      </div>
                  </div>
                </>
                )}
              </Col>
            
          ) : (
            <>
              <Col xs={9} md={7} lg={5} className='mb-5 mb-lg-0'>
                {/* <SVG src={anonymousAvatar} className='mw-100' /> */}
              </Col>
              <Col
                xs={12}
                lg={7}
                xl={6}
                className='first-review text-lg-left'
                >
                <div className='anon-review-image'></div>
                <div className='first-review-content'>
                  <div className='review__content'>
                    {firstReview.reviewContent}
                  </div>
                  <div className='review__name mb-0'>
                    {firstReview.reviewAuthor}
                  </div>
                  <div className='review__location'>
                    {`${franchisee.name}, ${franchisee.state}`}
                  </div>
                </div>
              </Col>
            </>
            
          )}
        </Section>
        <Section padding='bottom'>
          <div
            id='reviews-accordion'
            className='reviews__accordion accordion review-stars__trigger'
          >
            {businessesId === null || storeReviews.length === 0 ? (
              <>
                {reviews.defaultReviews.map((review, index) =>
                  index >= 1 ? (
                    <Card className='reviews__review' key={review.name}>
                      <Card.Header>
                        <Row className='flex-md-nowrap justify-content-between justify-content-md-center'>
                          {/* <Col className='align-self-center d-none d-lg-block'></Col> */}
                          <Col className='align-self-center'>
                            <small className='text-muted font-weight-bold'>
                              {review.name}
                            </small>
                            <StaticReviewStars />
                            <div className='mt-1'>
                              {review.content}
                            </div>
                          </Col>
                        </Row>
                      </Card.Header>
                    </Card>
                  ) : null
                )}
              </>
            ) : (
              <>
                {franchisee.reviewFacebookUrl === '' ? null : (
                  <Card.Header
                    className='reviews-cta'
                    style={{ padding: '2.5rem 4.5rem' }}
                  >
                    <Row className='reviews-stars-cta-row align-items-center justify-content-center'>
                      {/* <Row className='stars-row align-items-center'>
                        <small className='text-muted'>
                          {storeReviews.length} Reviews
                        </small>
                      </Row> */}

                      <Row className='reviews-buttons'>
                        {franchisee.reviewGooglePlusUrl && (
                          <div className='first-review-button text-center mx-3 my-1'>
                            <a
                              href={franchisee.reviewGooglePlusUrl}
                              style={{ padding: '.5rem 1rem' }}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='btn-primary btn review-button'
                            >
                              Review us on Google
                            </a>
                          </div>
                        )}

                        {franchisee.reviewFacebookUrl && (
                          <div className='text-center mx-3 my-1'>
                            <a
                              href={franchisee.reviewFacebookUrl}
                              style={{ padding: '.5rem 1rem' }}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='btn-primary btn review-button'
                            >
                              Review us on Facebook
                            </a>
                          </div>
                        )}
                      </Row>
                    </Row>
                  </Card.Header>
                )}
                {currentReviews.map((review, index) =>
                  index >= 1 ? (
                    <Card className='reviews__review' key={review.reviewId}>
                      <Card.Header>
                        <Row className='flex-md-nowrap justify-content-between justify-content-md-center'>
                          {/* <Col className='align-self-center d-none d-lg-block'></Col> */}
                          <Col className='align-self-center'>
                            <small className='review__name font-weight-bold'>
                              {review.reviewAuthor}
                            </small>
                            <StaticReviewStars />
                            <div className='mt-1'>
                              {review.reviewContent}
                            </div>
                          </Col>
                        </Row>
                      </Card.Header>
                    </Card>
                  ) : null
                )}
                <div style={{display: 'flex', justifyContent: 'center'}}>
                  <ReviewsPagination
                    reviewsPerPage={reviewsPerPage}
                    totalReviews={storeReviews.length}
                    paginate={paginate}
                    currentPage={currentPage}
                    />
                </div>
              </>
            )}
          </div>
        </Section>

        {/* <Section padding='top' className='pb-5 pb-md-0'>
          <ReviewLinks />
        </Section> */}
      </Layout>
    </FranchiseeProvider>
  );
};

export default Reviews;
